import { useState, useEffect } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="https://public.readdy.ai/ai/img_res/cc998864-7b0a-4f9d-8761-0e0d04f04c9e.png" alt="YAGUARETE S.R.L." className="h-12 w-auto" />
          <div className="flex gap-8">
            <a href="#home" className={`text-sm font-medium transition-colors cursor-pointer ${scrolled ? 'text-gray-900 hover:text-orange-500' : 'text-white hover:text-orange-300'}`}>Home</a>
            <a href="#about" className={`text-sm font-medium transition-colors cursor-pointer ${scrolled ? 'text-gray-900 hover:text-orange-500' : 'text-white hover:text-orange-300'}`}>About</a>
            <a href="#products" className={`text-sm font-medium transition-colors cursor-pointer ${scrolled ? 'text-gray-900 hover:text-orange-500' : 'text-white hover:text-orange-300'}`}>Products</a>
            <a href="#features" className={`text-sm font-medium transition-colors cursor-pointer ${scrolled ? 'text-gray-900 hover:text-orange-500' : 'text-white hover:text-orange-300'}`}>Features</a>
            <a href="#contact" className={`text-sm font-medium transition-colors cursor-pointer ${scrolled ? 'text-gray-900 hover:text-orange-500' : 'text-white hover:text-orange-300'}`}>Contact</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/3a0671da041b619e2e1ba5ed8a7231db.jpg" 
            alt="Hero Background" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="flex items-center min-h-screen py-32">
            <div className="w-full text-center">
              <h1 className="text-7xl md:text-8xl font-thin text-white tracking-wider mb-8">
                YAGUARETE S.R.L.
              </h1>
              <p className="text-xl text-white/85 tracking-wide font-light mb-12">
                Crafting Premium iOS Experiences
              </p>
              <p className="text-lg text-white/75 max-w-3xl mx-auto leading-relaxed">
                A team of experts developing modern applications for the iOS platform. Our products are designed to enhance user experience and improve the security of mobile devices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-20">
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-20">Who We Are</p>
            <div className="flex justify-end">
              <div className="w-full md:w-[55%]">
                <h2 className="text-6xl md:text-7xl font-extrabold text-[#1A1A1A] leading-[1.1] mb-12">
                  Expert Team Building Modern iOS Applications
                </h2>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices. We combine cutting-edge technology with intuitive design to deliver exceptional iOS applications.
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex gap-8 mt-16">
            <div className="w-[38%]">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20software%20development%20team%20collaborating%20in%20modern%20office%2C%20diverse%20developers%20working%20on%20iOS%20applications%2C%20MacBooks%20and%20iPhones%20on%20desk%2C%20bright%20natural%20lighting%2C%20contemporary%20workspace%2C%20teamwork%20atmosphere%2C%20high%20quality%20photography&width=600&height=700&seq=about-img-001&orientation=portrait" 
                alt="Team Collaboration" 
                className="w-full h-[500px] object-cover object-top rounded-3xl shadow-lg"
              />
            </div>
            <div className="w-[58%]">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20tech%20office%20interior%20with%20iOS%20development%20workspace%2C%20clean%20minimalist%20design%2C%20large%20windows%20with%20natural%20light%2C%20contemporary%20furniture%2C%20professional%20atmosphere%2C%20wide%20angle%20view%2C%20premium%20quality%20photography&width=900&height=700&seq=about-img-002&orientation=landscape" 
                alt="Modern Office" 
                className="w-full h-[500px] object-cover object-top rounded-3xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold text-[#1A1A1A] mb-6">Our Products</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Powerful iOS applications designed for security and performance
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            {/* VPN Product Card */}
            <div className="bg-gradient-to-br from-[#EEF2FF] to-[#E0E7FF] rounded-[32px] p-16">
              <div className="w-20 h-20 flex items-center justify-center mb-10">
                <i className="ri-shield-check-line text-6xl text-indigo-600"></i>
              </div>
              <h3 className="text-4xl font-bold text-[#1A1A1A] mb-6">VPN Protection</h3>
              <p className="text-base text-gray-700 leading-relaxed">
                Secure your connection with military-grade encryption. Browse privately across global servers. Protect your data on any network with our advanced VPN technology designed specifically for iOS devices.
              </p>
            </div>

            {/* Cleaner Product Card */}
            <div className="bg-gradient-to-br from-[#F0FDF4] to-[#DCFCE7] rounded-[32px] p-16">
              <div className="w-20 h-20 flex items-center justify-center mb-10">
                <i className="ri-refresh-line text-6xl text-green-600"></i>
              </div>
              <h3 className="text-4xl font-bold text-[#1A1A1A] mb-6">Device Cleaner</h3>
              <p className="text-base text-gray-700 leading-relaxed">
                Optimize storage with intelligent cleaning. Remove duplicate photos and large files. Keep your device running smoothly with our comprehensive cleaning solution for iOS.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.25em] text-gray-400 mb-8">Core Features</p>
            <h2 className="text-5xl md:text-6xl font-extrabold text-[#0A0A0A] leading-tight max-w-[600px]">
              Built for Performance and Security
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-10 mt-16">
            {/* Feature 1 */}
            <div className="bg-[#F3F4F6] rounded-[20px] p-12">
              <div className="w-14 h-14 flex items-center justify-center mb-8">
                <i className="ri-lock-2-line text-5xl text-[#1A1A1A]"></i>
              </div>
              <h3 className="text-2xl font-semibold text-[#1A1A1A] mb-5">Advanced Encryption</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                Bank-level security protocols protect your data transmission with AES-256 encryption standards
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-[#F3F4F6] rounded-[20px] p-12">
              <div className="w-14 h-14 flex items-center justify-center mb-8">
                <i className="ri-speed-line text-5xl text-[#1A1A1A]"></i>
              </div>
              <h3 className="text-2xl font-semibold text-[#1A1A1A] mb-5">Performance Optimization</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                Intelligent algorithms ensure maximum speed while maintaining complete security and privacy
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-[#F3F4F6] rounded-[20px] p-12">
              <div className="w-14 h-14 flex items-center justify-center mb-8">
                <i className="ri-smartphone-line text-5xl text-[#1A1A1A]"></i>
              </div>
              <h3 className="text-2xl font-semibold text-[#1A1A1A] mb-5">Device Protection</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                Comprehensive security suite guards against threats while optimizing system performance
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Premium%20workspace%20desk%20top%20view%20with%20iPhone%20displaying%20modern%20app%20interface%2C%20warm%20natural%20lighting%20from%20window%2C%20minimalist%20aesthetic%2C%20professional%20photography%2C%20soft%20shadows%2C%20contemporary%20office%20setting%2C%20high%20end%20quality%2C%20clean%20composition&width=1920&height=1080&seq=cta-bg-001&orientation=landscape" 
            alt="CTA Background" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/25"></div>
        </div>
        
        <div className="relative z-10 text-center px-6">
          <h2 className="text-7xl md:text-8xl font-extrabold text-white tracking-[0.08em] mb-10">
            ELEVATE YOUR<br />iOS EXPERIENCE
          </h2>
          <p className="text-xl md:text-2xl text-white font-light tracking-wide mb-4">
            Professional applications designed for modern users
          </p>
          <p className="text-xl md:text-2xl text-white font-light tracking-wide">
            Security and performance in perfect harmony
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold text-[#1A1A1A] mb-6">Get In Touch</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Have questions about our products? We're here to help
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-16 max-w-5xl mx-auto">
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-3xl text-orange-500"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-[#1A1A1A] mb-2">Address</h4>
                  <p className="text-base text-gray-600">SARMIENTO 609</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-line text-3xl text-orange-500"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-[#1A1A1A] mb-2">City</h4>
                  <p className="text-base text-gray-600">La Falda</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-3xl text-orange-500"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-[#1A1A1A] mb-2">Email</h4>
                  <p className="text-base text-gray-600">support@yaguarete-srl.com</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <i className="ri-inbox-line text-3xl text-orange-500"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-[#1A1A1A] mb-2">Zip</h4>
                  <p className="text-base text-gray-600">X5176BBN</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <i className="ri-global-line text-3xl text-orange-500"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-[#1A1A1A] mb-2">Country</h4>
                  <p className="text-base text-gray-600">Argentina</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-3xl text-orange-500"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-[#1A1A1A] mb-2">Number</h4>
                  <p className="text-base text-gray-600">+1 3603647888</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F172A] rounded-t-[32px] mx-6 mt-24 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 right-0 text-[180px] font-extrabold text-[#1E293B]/40 leading-none text-center pointer-events-none">
          YAGUARETE
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-12 py-20">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* Brand Column */}
            <div>
              <h3 className="text-3xl font-thin text-white mb-32">YAGUARETE S.R.L.</h3>
              <p className="text-sm text-white/70">iOS Development Excellence</p>
            </div>
            
            {/* Navigation Column */}
            <div>
              <h4 className="text-base font-medium text-white uppercase tracking-[0.1em] mb-6">Explore</h4>
              <div className="space-y-4">
                <a href="#home" className="block text-sm text-[#94A3B8] hover:text-white transition-colors cursor-pointer">Home</a>
                <a href="#about" className="block text-sm text-[#94A3B8] hover:text-white transition-colors cursor-pointer">About Us</a>
                <a href="#products" className="block text-sm text-[#94A3B8] hover:text-white transition-colors cursor-pointer">Products</a>
                <a href="#features" className="block text-sm text-[#94A3B8] hover:text-white transition-colors cursor-pointer">Features</a>
                <a href="#contact" className="block text-sm text-[#94A3B8] hover:text-white transition-colors cursor-pointer">Contact</a>
              </div>
            </div>
            
            {/* Contact Column */}
            <div>
              <h4 className="text-base font-medium text-white uppercase tracking-[0.1em] mb-6">Contact</h4>
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <i className="ri-map-pin-line text-xl text-[#94A3B8]"></i>
                  <span className="text-sm text-[#94A3B8]">SARMIENTO 609</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-building-line text-xl text-[#94A3B8]"></i>
                  <span className="text-sm text-[#94A3B8]">La Falda</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-inbox-line text-xl text-[#94A3B8]"></i>
                  <span className="text-sm text-[#94A3B8]">X5176BBN</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-global-line text-xl text-[#94A3B8]"></i>
                  <span className="text-sm text-[#94A3B8]">Argentina</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-mail-line text-xl text-[#94A3B8]"></i>
                  <span className="text-sm text-[#94A3B8]">support@yaguarete-srl.com</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-phone-line text-xl text-[#94A3B8]"></i>
                  <span className="text-sm text-[#94A3B8]">+1 3603647888</span>
                </div>
              </div>
            </div>
            
            {/* Newsletter Column */}
            <div>
              <p className="text-sm text-white leading-relaxed mb-8">
                Stay updated with our latest<br />iOS development insights
              </p>
              <div className="flex items-center">
                <input 
                  type="email" 
                  placeholder="Your email" 
                  className="flex-1 bg-[#1E293B] border border-[#334155] rounded-l-xl h-[52px] px-5 text-sm text-white placeholder-[#64748B] focus:outline-none focus:border-orange-500 transition-colors"
                />
                <button className="w-[52px] h-[52px] bg-white rounded-r-xl flex items-center justify-center hover:bg-orange-500 transition-colors cursor-pointer">
                  <i className="ri-arrow-right-line text-xl text-black"></i>
                </button>
              </div>
            </div>
          </div>
          
          <div className="mt-20 pt-8 border-t border-white/10 text-center">
            <p className="text-sm text-[#94A3B8]">
              © 2025 YAGUARETE S.R.L. All rights reserved. | <a href="https://readdy.ai/?ref=logo" className="hover:text-white transition-colors cursor-pointer">Powered by Readdy</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
